import InfiniteScroll from "@/modern/components/infinite-scroll";
import { ChecklistProgress } from "./ChecklistProgress";
import { CheckSquare, ChevronRight, Loader2, PencilIcon } from "lucide-react";
import { cn } from "@/modern/utils";
import { useEntityChecklistContext } from "../context/EntityChecklistContext";
import type { Checklist } from "../types";
import { ChecklistAssignedAvatars } from "@/modern/components/board-user-select-field";

interface ChecklistListProps {
  checklists: Checklist[];
  onShowDetails: (checklist: Checklist) => void;
}

export const ChecklistList = ({ checklists, onShowDetails }: ChecklistListProps) => {
  const {
    canEditChecklist,
    isUserAssignedToChecklist,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useEntityChecklistContext();

  return (
    <div className="flex flex-col">
      {checklists.map((checklist) => {
        const assignedCount = checklist.assignedBoardUsers?.length ?? 0;
        return (
          <button
            key={checklist.id}
            type="button"
            onClick={() => onShowDetails(checklist)}
            className={cn(
              "group w-full p-4 bg-card rounded-lg border border-border/70 text-left mt-3 first:mt-0 transition-all duration-200 hover:border-primary/60 hover:bg-primary/5",
              checklist.archived && "line-through"
            )}
          >
            <div className="flex items-start gap-2">
              <div className="p-1 rounded-md bg-primary/20 text-primary shrink-0">
                <CheckSquare className="size-4" />
              </div>
              <div className="flex-1 min-w-0 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="flex items-center gap-2 font-medium text-foreground/95 truncate text-sm min-w-0">
                    <span className="truncate">{checklist.title || "Untitled Checklist"}</span>
                    {(canEditChecklist(checklist) || isUserAssignedToChecklist(checklist)) && (
                      <PencilIcon className="size-3 shrink-0" />
                    )}
                  </h4>
                  <div className="flex items-center gap-2 shrink-0">
                    {assignedCount > 0 ? <ChecklistAssignedAvatars checklist={checklist} max={3} /> : null}
                    <ChevronRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  </div>
                </div>
                <ChecklistProgress checklist={checklist} barContainerClassName="h-1.5" textClassName="text-xs" />
              </div>
            </div>
          </button>
        );
      })}
      {(hasNextPage || isFetchingNextPage) && (
        <InfiniteScroll
          hasMore={hasNextPage}
          isLoading={isFetchingNextPage}
          next={fetchNextPage}
          rootMargin="0px"
          threshold={0}
        >
          <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
            {isFetchingNextPage ? (
              <>
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
                Loading more…
              </>
            ) : null}
          </div>
        </InfiniteScroll>
      )}
    </div>
  );
};
